import { useSelector } from 'react-redux';
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Avatar, Box, CircularProgress, Container, Stack, Typography } from '@mui/material';
import LineChart from '../components/chart/LineChart';
import './scss/memberViewPage.scss';
import Members from '../components/reusableTables/Members';

import { API_BASE_URL } from '../utils/env';

const MemberViewPage = () => {
  const { memberId } = useParams();
  const navigate = useNavigate();

  const [memberDetails, setMemberDetails] = useState([]);
  const [loader, setLoader] = useState(true);
  const [error, setError] = useState(null);

  const [memberReport, setMemberReport] = useState([]);
  const [membmerReportLoader, setMemberReportLoader] = useState(true);
  const [memberReportError, setMemberReportError] = useState(null);

  // Function to fetch data from the API
  async function fetchPatientDetailsById(id, token) {
    try {
      const response = await fetch(`${API_BASE_URL}/Member/GetMemberDetailsByid?memberId=${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        setError('Network Error');
        return null;
      }
      const data = await response.json();
      return data;
    } catch (error) {
      setError(error.message);
      console.error(error.message);
      return null;
    }
  }

  const { userInfo } = useSelector((state) => state.auth);

  useEffect(() => {
    // Fetch the data when the component mounts
    fetchPatientDetailsById(memberId, userInfo.token).then((data) => {
      if (data) {
        setMemberDetails(data);
      } else {
        console.log('Failed to fetch data.');
        setError('Failed to fetch data.');
      }
      // Set loading to false once the data fetching process is complete
      setLoader(false);
    });
  }, [memberId, userInfo.token]);

  function formatDate(inputDate) {
    const date = new Date(inputDate);

    // Extract date components
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Month is zero-based
    const day = String(date.getDate()).padStart(2, '0');

    // Format the date components
    const formattedDate = `${day} | ${month} | ${year}`;

    return formattedDate;
  }

  function extractYearFromDate(inputDate) {
    const date = new Date(inputDate);

    // Extract the year component
    const year = date.getFullYear();

    return year;
  }

  function formatDateOfBirth(inputDate) {
    // Split the input string into day, month, and year components
    const [day, month, year] = inputDate.split(' | ');

    // Create a new Date object using the parsed components
    const date = new Date(`${month}/${day}/${year}`);

    // Get the month name and day from the Date object
    const monthName = date.toLocaleString('default', { month: 'long' });
    const formattedDay = date.getDate();

    // Concatenate the month name and day into the desired format
    const formattedDate = `${monthName} ${formattedDay}`;

    return formattedDate;
  }

  function formatAnniversaryDate(inputDate) {
    // Split the input string into day, month, and year components
    const [day, month, year] = inputDate.split(' | ');

    // Create a new Date object using the parsed components
    const date = new Date(`${month}/${day}/${year}`);

    // Get the month name and day from the Date object
    const monthName = date.toLocaleString('default', { month: 'long' });
    const formattedDay = date.getDate();

    // Concatenate the month name and day into the desired format
    const formattedDate = `${monthName} ${formattedDay}`;

    return formattedDate;
  }

  useEffect(() => {
    const fetchMemberReport = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/Report/GetMemberReport?memberId=${memberId}`, {
          headers: {
            Authorization: `Bearer ${userInfo.token}`,
            'Content-Type': 'application/json',
          },
        });
        if (!response.ok) {
          // Attempt to capture the error message from the response
          const errorResponse = await response.json();
          const errorMessage = errorResponse.error;
          throw new Error(errorMessage);
        }
        const responseData = await response.json();

        if (responseData.success && responseData === null) {
          // Handle the case where the API returns null for data
          setMemberReport([]);
          setMemberReportLoader(false);
          setMemberReportError(responseData.message);
        } else {
          // Handle the case where data is not null
          setMemberReport(responseData);
          setMemberReportLoader(false);
          setMemberReportError(null);
        }
      } catch (error) {
        setMemberReportLoader(false);
        setMemberReportError(error.message);
      }
    };

    fetchMemberReport();
  }, [memberId, userInfo.token]);

  return (
    <Container>
      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={'20px'}>
          <Typography fontSize={25} fontWeight={700} color="#11142d">
            Bio Data
          </Typography>
        </Stack>
        {loader || membmerReportLoader ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', allignItems: 'center', height: '100%' }}>
            <CircularProgress sx={{ color: '#000080' }} />
          </Box>
        ) : (
          <>
            {error ? (
              <p>Error: {error}</p>
            ) : (
              <>
                <Box
                  display="flex"
                  gap="20px"
                  className="hhh"
                // flexDirection={{xs: 'column'}}
                >
                  {/* left side */}
                  <Box
                    position="relative"
                    flex={'1'}
                    padding="20px"
                    boxShadow="2px 4px 10px 1px rgba(201,201,201,0.47)"
                    borderRadius="15px"
                    bgcolor={'#fcfcfc'}
                  >
                    <Typography
                      className="editButton"
                      onClick={() => navigate(`/dashboard/members/${memberId}/memberedit`)}
                    >
                      Edit
                    </Typography>
                    <Typography fontSize={'20px'} fontWeight={'700'} color="#808191" mb={'20px'}>
                      Information
                    </Typography>

                    <div className="item">
                      <Avatar alt={`${memberDetails.data.lastName}`} src='./lazyPath' className="itemImg" />
                      <div className="details">
                        <Typography className="itemTitle">
                          {`${memberDetails.data.lastName} ${memberDetails.data.firstName}`}
                        </Typography>

                        <Box className="detailItem">
                          <span className="itemKey">Gender:</span>
                          <span className="itemValue">{`${memberDetails.data.gender}`}</span>
                        </Box>

                        <Box className="detailItem">
                          <span className="itemKey">Email:</span>
                          <span className="itemValue"> {`${memberDetails.data.email}`}</span>
                        </Box>

                        <Box className="detailItem">
                          <span className="itemKey">Phone:</span>
                          <span className="itemValue">{`${memberDetails.data.phoneNumber}`}</span>
                        </Box>

                        <Box className="detailItem">
                          <span className="itemKey">Date of birth:</span>
                          <span className="itemValue">{`${formatDateOfBirth(
                            formatDate(memberDetails.data.dateOfBirth)
                          )}`}</span>
                          {/* <span className="itemValue">01 | 23 | 2004</span> */}
                        </Box>

                        <Box className="detailItem">
                          <span className="itemKey">Department:</span>
                          <span className="itemValue">{`${memberDetails.data.department}`}</span>
                        </Box>

                        <Box className="detailItem">
                          <span className="itemKey">Member type:</span>
                          <span className="itemValue">{`${memberDetails.data.memberType}`}</span>
                        </Box>

                        <Box className="detailItem">
                          <span className="itemKey">Name of next of kin:</span>
                          <span className="itemValue">{`${memberDetails.data.nextOfKin}`}</span>
                        </Box>

                        <Box className="detailItem">
                          <span className="itemKey">Number of next of kin:</span>
                          <span className="itemValue">{`${memberDetails.data.nextOfKinPhone}`}</span>
                        </Box>

                        <Box className="detailItem">
                          <span className="itemKey">Year joined:</span>
                          <span className="itemValue">{`${extractYearFromDate(memberDetails.data.yearJoined)}`}</span>
                        </Box>

                        {memberDetails.data.marriageAnniversary && (
                          <Box className="detailItem">
                            <span className="itemKey">Marriage anniversary:</span>
                            <span className="itemValue">
                              {formatAnniversaryDate(formatDate(memberDetails.data.marriageAnniversary))}
                            </span>
                          </Box>
                        )}

                        <Box className="detailItem">
                          <span className="itemKey">Address:</span>
                          <span className="itemValue">{`${memberDetails.data.address}`}</span>
                        </Box>
                        {memberDetails.data.state && (
                          <Box className="detailItem">
                            <span className="itemKey">State of origin:</span>
                            <span className="itemValue">{`${memberDetails.data.state}`}</span>
                          </Box>
                        )}

                        <Box className="detailItem">
                          <span className="itemKey">Country:</span>
                          <span className="itemValue">{`${memberDetails.data.country}`}</span>
                        </Box>

                        {memberDetails.data.biometricId === null && (
                          <Box className="detailItem">
                            <span className="itemKey">Biometric ID:</span>
                            <span className="itemValue">{`${memberDetails.data.biometricId}`}</span>
                          </Box>
                        )}

                      </div>
                    </div>
                  </Box>

                  {/* right side */}
                  <Box
                    flex="2"
                    padding={'20px'}
                    bgcolor="#fcfcfc"
                    borderRadius={'15px'}
                    boxShadow="2px 4px 10px 1px rgba(201,201,201,0.47)"
                  >
                    <LineChart
                      firstName={`${memberDetails.data.firstName}`}
                      lastName={`${memberDetails.data.lastName}`}
                      memberReport={memberReport}
                    />
                  </Box>
                </Box>

                <Box mt={'20px'}>
                  <Members />
                </Box>
              </>
            )}
          </>
        )}
      </Box>
    </Container>
  );
};

export default MemberViewPage;
